// The access token lives in memory only, never in storage: an XSS cannot lift it
// from localStorage, and it dies with the tab. The auth store sets it.
let accessToken: string | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

/**
 * Custom error class for HTTP responses.
 */
export class HttpError extends Error {
  status: number
  data: unknown

  constructor(message: string, status: number, data: unknown) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.data = data
  }
}

function extractErrorMessage(data: unknown): string {
  if (!data || typeof data !== 'object') return 'Network error. Please try again.'
  const d = data as Record<string, unknown>

  if (d.error) return String(d.error)
  if (d.detail) return String(d.detail)
  if (d.message) return String(d.message)

  // DRF validation errors: { field: ["error msg", ...], non_field_errors: [...], ... }
  const messages = Object.entries(d).flatMap(([field, errors]) => {
    if (Array.isArray(errors)) return errors.map((e) => `${field}: ${String(e)}`)
    if (typeof errors === 'string') return [`${field}: ${errors}`]
    return []
  })
  return messages.length > 0 ? messages.join('\n') : 'Network error. Please try again.'
}

// Shared state to track token refresh status and queue concurrent requests
let isRefreshing = false
let refreshPromise: Promise<void> | null = null

/**
 * Helper to handle 401 refreshes shared between fetch wrappers.
 * Attempts to refresh the token. If successful, the promise resolves.
 * If failed, it throws, allowing the caller to handle the logout/error.
 */
async function handle401Refresh(): Promise<void> {
  // Lazy-import to avoid circular dependencies
  const { useAuthStore } = await import('@/stores/auth')
  const auth = useAuthStore()

  if (!isRefreshing) {
    isRefreshing = true
    refreshPromise = auth
      .refreshToken()
      .catch((error) => {
        // Refresh failed (e.g. refresh token also expired)
        auth.logout()
        throw error
      })
      .finally(() => {
        isRefreshing = false
        refreshPromise = null
      })
  }

  if (refreshPromise) {
    await refreshPromise
  }
}

/**
 * Esegue una chiamata API autenticata aggiungendo il token Bearer all'header Authorization.
 * @param {string} endpoint - L'endpoint API da chiamare.
 * @param {RequestInit} [options] - Le opzioni della richiesta fetch.
 * @param {boolean} [rawResponse=false] - Se true, restituisce la risposta raw invece di JSON.
 * @returns {Promise<unknown>} La risposta JSON dal backend o la risposta raw.
 */
export async function authFetch(
  endpoint: string,
  options: RequestInit = {},
  rawResponse = false,
): Promise<unknown> {
  return _authFetch(endpoint, options, rawResponse, false)
}

async function _authFetch(
  endpoint: string,
  options: RequestInit,
  rawResponse: boolean,
  hasRetried: boolean,
): Promise<unknown> {
  const baseURL = import.meta.env.VITE_API_URL

  if (!baseURL) {
    throw new Error("VITE_API_URL is not set in the .env file.")
  }

  const finalURL = new URL(endpoint.startsWith('/') ? endpoint.substring(1) : endpoint, baseURL)
    .href

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  }

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  // Django's cache_page sends `Cache-Control: max-age`; without 'no-cache' the
  // browser reuses responses for an hour, even after the server cache is cleared.
  const response = await fetch(finalURL, { cache: 'no-cache', ...options, headers })

  if (!response.ok) {
    if (response.status === 401 && !hasRetried) {
      try {
        await handle401Refresh()
        return await _authFetch(endpoint, options, rawResponse, true)
      } catch (error) {
        console.error('Token refresh failed during authFetch retry:', error)
      }
    }

    const errorData = await response.json().catch(() => ({ message: 'The request failed. Please try again.' }))
    throw new HttpError(extractErrorMessage(errorData), response.status, errorData)
  }

  if (rawResponse) return response
  if (response.status === 204) return null
  return response.json()
}

/**
 * Esegue una chiamata API per la funzionalità di query NLP.
 * Utilizza un endpoint e una chiave API specifici per il server.
 * @param {string} endpoint - L'endpoint API da chiamare (es. '/query').
 * @param {RequestInit} [options] - Le opzioni della richiesta fetch.
 * @returns {Promise<Response>} La risposta raw dal backend.
 */
export async function queryFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
  return _queryFetch(endpoint, options, false)
}

async function _queryFetch(
  endpoint: string,
  options: RequestInit,
  hasRetried: boolean,
): Promise<Response> {
  const baseURL = import.meta.env.VITE_API_URL

  if (!baseURL) {
    throw new Error("VITE_API_URL is not set in the .env file.")
  }

  const finalURL = new URL(endpoint.startsWith('/') ? endpoint.substring(1) : endpoint, baseURL)
    .href

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  // Django's cache_page sends `Cache-Control: max-age`; without 'no-cache' the
  // browser reuses responses for an hour, even after the server cache is cleared.
  const response = await fetch(finalURL, { cache: 'no-cache', ...options, headers })

  if (response.status === 401 && !hasRetried) {
    try {
      await handle401Refresh()
      return await _queryFetch(endpoint, options, true)
    } catch (error) {
      console.error('Token refresh failed during queryFetch retry:', error)
      return response
    }
  }

  // Refresh succeeded but the server still returns 401 — session revoked server-side.
  if (response.status === 401) {
    const { useAuthStore } = await import('@/stores/auth')
    useAuthStore().logout()
  }

  return response
}

/**
 * Esegue una chiamata API per la funzionalità di query NLP.
 * Utilizza un endpoint e una chiave API specifici per il server.
 * @param {string} endpoint - L'endpoint API da chiamare (es. '/query').
 * @param {RequestInit} [options] - Le opzioni della richiesta fetch.
 * @returns {Promise<Response>} La risposta raw dal backend.
 */
export async function queryFetchLocalServer(
  endpoint: string,
  options: RequestInit = {},
): Promise<Response> {
  const baseURL = 'http://localhost:8001/'
  // const apiKey = import.meta.env.VITE_API_KEY

  if (!baseURL) {
    throw new Error(
      "VITE_API_URL or VITE_API_KEY is not set in the .env file.",
    )
  }

  const finalURL = new URL(endpoint.startsWith('/') ? endpoint.substring(1) : endpoint, baseURL)
    .href

  const headers: Record<string, string> = {
    // 'X-API-KEY': apiKey,
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  // Not applying refresh logic here as local server might not use the same auth flow
  // or is strictly dev-only.
  return await fetch(finalURL, {
    ...options,
    headers,
  })
}

/**
 * Downloads a file from the given endpoint.
 */
export async function downloadFile(endpoint: string, params: URLSearchParams) {
  try {
    const response = (await authFetch(`${endpoint}?${params.toString()}`, {}, true)) as Response
    const contentDisposition = response.headers.get('content-disposition')
    let filename = 'download.xlsx'
    if (contentDisposition) {
      let match = contentDisposition.match(/filename="([^"]+)"/)
      if (!match) match = contentDisposition.match(/filename=([^;]+)/)
      if (match && match[1]) filename = match[1].trim()
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading file:', error)
    throw error
  }
}
